import { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AuthorDetails } from '../types';
import { apiRequest } from '@/lib/queryClient';

interface UserProfile {
  id: number;
  email: string;
  firstName?: string;
  lastName?: string;
  authorName?: string;
  authorBio?: string;
  authorWebsite?: string;
  authorSocialMedia?: string;
  authorContact?: string;
  authorPenName?: string;
  authorProfessionalCredentials?: string;
  authorGenreSpecialization?: string;
  authorProfileImage?: string;
}

interface ProfileImportHook {
  importFromProfile: () => Promise<AuthorDetails>;
  enrichMissingFields: (partial: Partial<AuthorDetails>, projectContext?: any) => Promise<AuthorDetails>;
  isImporting: boolean;
  profileData: UserProfile | null;
  profileLoading: boolean;
  profileError: string | null;
}

export const useProfileImport = (): ProfileImportHook => {
  const [isImporting, setIsImporting] = useState(false);

  // Fetch user profile data
  const { data: profileResponse, isLoading: profileLoading, error } = useQuery<{ user: UserProfile }>({
    queryKey: ['/api/user/profile'],
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const profileData = profileResponse?.user || null;
  const profileError = error ? 'Failed to load profile data' : null;

  // Map profile data to AuthorDetails format
  const mapProfileToAuthorDetails = useCallback((profile: UserProfile): AuthorDetails => {
    // Smart field mapping with fallbacks
    const authorName = profile.authorName ||
                      profile.authorPenName ||
                      (profile.firstName && profile.lastName ? `${profile.firstName} ${profile.lastName}` : '') ||
                      '';

    // Get current year for copyright
    const currentYear = new Date().getFullYear().toString();

    const mappedData = {
      authorName,
      authorBio: profile.authorBio || '',
      authorWebsite: profile.authorWebsite || '',
      authorSocialMedia: profile.authorSocialMedia || '',
      authorContact: profile.authorContact || '',
      authorProfileImageBase64: profile.authorProfileImage || null, // Import profile image from user profile
      // Auto-populated copyright & publishing defaults
      copyrightYear: currentYear,
      isbn: '',
      publisher: 'Independent Publishing',
      edition: 'First Edition',
      copyrightNotice: 'All rights reserved. No part of this book may be reproduced, distributed, or transmitted in any form or by any means, including photocopying, recording, or other electronic or mechanical methods, without the prior written permission of the publisher, except in the case of brief quotations embodied in critical reviews and certain other noncommercial uses permitted by copyright law.',
      publisherLogoBase64: null,
      frontCoverEndorsements: '',
      awardsAndBadges: [],
      backCoverHookLine: '',
      backCoverEndorsements: '',
      seriesDisplayInfo: '',
      qrCodeUrl: '',
      socialMediaHandles: '',
    };

    console.log('🔍 [PROFILE-IMPORT] Mapped profile data:', {
      original: profile,
      mapped: mappedData
    });

    return mappedData;
  }, []);

  // Import profile data directly
  const importFromProfile = useCallback(async (): Promise<AuthorDetails> => {
    if (!profileData) {
      throw new Error('No profile data available to import');
    }

    setIsImporting(true);
    try {
      const authorDetails = mapProfileToAuthorDetails(profileData);
      return authorDetails;
    } finally {
      setIsImporting(false);
    }
  }, [profileData, mapProfileToAuthorDetails]);

  // Enrich missing fields with AI
  const enrichMissingFields = useCallback(async (
    partial: Partial<AuthorDetails>,
    projectContext?: any
  ): Promise<AuthorDetails> => {
    setIsImporting(true);
    
    try {
      // Identify missing fields
      const missingFields: (keyof AuthorDetails)[] = [];
      const fieldChecks: Array<{ key: keyof AuthorDetails; isEmpty: (val: any) => boolean }> = [
        { key: 'authorName', isEmpty: (val) => !val || val.trim() === '' },
        { key: 'authorBio', isEmpty: (val) => !val || val.trim() === '' },
        { key: 'authorWebsite', isEmpty: (val) => !val || val.trim() === '' },
        { key: 'authorSocialMedia', isEmpty: (val) => !val || val.trim() === '' },
        { key: 'authorContact', isEmpty: (val) => !val || val.trim() === '' },
      ];

      fieldChecks.forEach(({ key, isEmpty }) => {
        if (isEmpty(partial[key])) {
          missingFields.push(key);
        }
      });

      if (missingFields.length === 0) {
        // No fields to enrich, return as-is with defaults
        const currentYear = new Date().getFullYear().toString();
        return {
          authorName: partial.authorName || '',
          authorBio: partial.authorBio || '',
          authorWebsite: partial.authorWebsite || '',
          authorSocialMedia: partial.authorSocialMedia || '',
          authorContact: partial.authorContact || '',
          authorProfileImageBase64: partial.authorProfileImageBase64 || null,
          // Include copyright defaults
          copyrightYear: partial.copyrightYear || currentYear,
          isbn: partial.isbn || '',
          publisher: partial.publisher || 'Independent Publishing',
          edition: partial.edition || 'First Edition',
          copyrightNotice: partial.copyrightNotice || 'All rights reserved. No part of this book may be reproduced, distributed, or transmitted in any form or by any means, including photocopying, recording, or other electronic or mechanical methods, without the prior written permission of the publisher, except in the case of brief quotations embodied in critical reviews and certain other noncommercial uses permitted by copyright law.',
          publisherLogoBase64: partial.publisherLogoBase64 || null,
          frontCoverEndorsements: partial.frontCoverEndorsements || '',
          awardsAndBadges: partial.awardsAndBadges || [],
          backCoverHookLine: partial.backCoverHookLine || '',
          backCoverEndorsements: partial.backCoverEndorsements || '',
          seriesDisplayInfo: partial.seriesDisplayInfo || '',
          qrCodeUrl: partial.qrCodeUrl || '',
          socialMediaHandles: partial.socialMediaHandles || '',
        };
      }

      // Build context for AI enrichment
      const enrichmentContext = {
        existingAuthorData: partial,
        missingFields,
        projectContext: projectContext || {},
        profileData: profileData || {},
      };

      // Call AI enrichment endpoint
      const rawResponse = await apiRequest('/api/llm/enrich-author-details', 'POST', {
        context: enrichmentContext,
        fieldsToEnrich: missingFields,
      });
      const enrichedData = await rawResponse.json();

      // Merge enriched data with existing data
      const currentYear = new Date().getFullYear().toString();
      const result: AuthorDetails = {
        authorName: partial.authorName || enrichedData.authorName || '',
        authorBio: partial.authorBio || enrichedData.authorBio || '',
        authorWebsite: partial.authorWebsite || enrichedData.authorWebsite || '',
        authorSocialMedia: partial.authorSocialMedia || enrichedData.authorSocialMedia || '',
        authorContact: partial.authorContact || enrichedData.authorContact || '',
        authorProfileImageBase64: partial.authorProfileImageBase64 || null,
        // Include copyright defaults
        copyrightYear: partial.copyrightYear || currentYear,
        isbn: partial.isbn || '',
        publisher: partial.publisher || 'Independent Publishing',
        edition: partial.edition || 'First Edition',
        copyrightNotice: partial.copyrightNotice || 'All rights reserved. No part of this book may be reproduced, distributed, or transmitted in any form or by any means, including photocopying, recording, or other electronic or mechanical methods, without the prior written permission of the publisher, except in the case of brief quotations embodied in critical reviews and certain other noncommercial uses permitted by copyright law.',
        publisherLogoBase64: partial.publisherLogoBase64 || null,
        frontCoverEndorsements: partial.frontCoverEndorsements || '',
        awardsAndBadges: partial.awardsAndBadges || [],
        backCoverHookLine: partial.backCoverHookLine || '',
        backCoverEndorsements: partial.backCoverEndorsements || '',
        seriesDisplayInfo: partial.seriesDisplayInfo || '',
        qrCodeUrl: partial.qrCodeUrl || '',
        socialMediaHandles: partial.socialMediaHandles || '',
      };

      return result;
    } catch (error) {
      console.error('Failed to enrich author details:', error);
      // Fallback: return partial data with copyright defaults
      const currentYear = new Date().getFullYear().toString();
      return {
        authorName: partial.authorName || '',
        authorBio: partial.authorBio || '',
        authorWebsite: partial.authorWebsite || '',
        authorSocialMedia: partial.authorSocialMedia || '',
        authorContact: partial.authorContact || '',
        authorProfileImageBase64: partial.authorProfileImageBase64 || null,
        // Include copyright defaults even in fallback
        copyrightYear: partial.copyrightYear || currentYear,
        isbn: partial.isbn || '',
        publisher: partial.publisher || 'Independent Publishing',
        edition: partial.edition || 'First Edition',
        copyrightNotice: partial.copyrightNotice || 'All rights reserved. No part of this book may be reproduced, distributed, or transmitted in any form or by any means, including photocopying, recording, or other electronic or mechanical methods, without the prior written permission of the publisher, except in the case of brief quotations embodied in critical reviews and certain other noncommercial uses permitted by copyright law.',
        publisherLogoBase64: partial.publisherLogoBase64 || null,
        frontCoverEndorsements: partial.frontCoverEndorsements || '',
        awardsAndBadges: partial.awardsAndBadges || [],
        backCoverHookLine: partial.backCoverHookLine || '',
        backCoverEndorsements: partial.backCoverEndorsements || '',
        seriesDisplayInfo: partial.seriesDisplayInfo || '',
        qrCodeUrl: partial.qrCodeUrl || '',
        socialMediaHandles: partial.socialMediaHandles || '',
      };
    } finally {
      setIsImporting(false);
    }
  }, [profileData]);

  return {
    importFromProfile,
    enrichMissingFields,
    isImporting,
    profileData,
    profileLoading,
    profileError,
  };
};

export default useProfileImport;